import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Users, X, Calendar } from 'lucide-react';
import { api } from '../lib/api';

const OfflineBatches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchBatches();
  }, [showActiveOnly]);

  const fetchBatches = async () => {
    try {
      const response = await api.getOfflineBatches({ active_only: showActiveOnly });
      setBatches(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedBatch) {
        await api.updateOfflineBatch(selectedBatch.id, formData);
        toast.success('Batch updated successfully');
      } else {
        await api.createOfflineBatch(formData);
        toast.success('Batch created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    try {
      await api.deleteOfflineBatch(id);
      toast.success('Batch deleted');
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const openEditModal = (batch) => {
    setSelectedBatch(batch);
    setFormData({
      name: batch.name,
      description: batch.description || '',
      start_date: batch.start_date || '',
      is_active: batch.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedBatch(null);
    setFormData({ name: '', description: '', start_date: '', is_active: true });
  };

  return (
    <div className="space-y-6" data-testid="offline-batches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Batches</h1>
          <p className="text-slate-600">Manage student batches for offline classes</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-600">Active Only</span>
          </label>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
            data-testid="add-batch-btn"
          >
            <Plus size={20} />
            New Batch
          </button>
        </div>
      </div>

      {/* Batches Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-8 text-center text-slate-500">Loading...</div>
        ) : batches.length === 0 ? (
          <div className="col-span-full p-8 text-center text-slate-500 bg-white rounded-xl border">
            No batches found. Create your first batch to get started.
          </div>
        ) : (
          batches.map((batch) => (
            <div
              key={batch.id}
              className={`bg-white rounded-xl border shadow-sm p-5 ${!batch.is_active ? 'opacity-60' : ''}`}
              data-testid={`batch-card-${batch.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">{batch.name}</h3>
                  {batch.description && (
                    <p className="text-sm text-slate-500 mt-1">{batch.description}</p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  batch.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {batch.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              {batch.start_date && (
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                  <Calendar size={16} />
                  <span>Started: {new Date(batch.start_date).toLocaleDateString()}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1 text-sm text-slate-500">
                  <Users size={16} />
                  <span>{batch.student_count || 0} students</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(batch)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Edit"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(batch.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">{selectedBatch ? 'Edit Batch' : 'New Batch'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Batch Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="e.g., SSC CGL 2026 Batch 1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  rows={3}
                  placeholder="Optional description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">Active Batch</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  {selectedBatch ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineBatches;
